import type { FeatureTwin } from 'src/models'
import { jexlEvalString } from 'src/utils/jexl'

/**
 * Parameter context for evaluating a feature's `label` or `title`: parameter values both as
 * top-level names and under `params`, the same shape the rest of the feature config uses.
 */
export const createFeatureExpressionContext = (
  parameterValues: Record<string, unknown>
): Record<string, unknown> => ({ ...parameterValues, params: parameterValues })

/**
 * Tab label: the feature's `label`, shown in the main navigation, the drawer tooltips, the
 * admin feature list and as a FeatureTabs child tab. Evaluated when it is an expression
 * ('= …' or a {{ }} template). Empty when the feature has no label.
 */
export const resolveFeatureTabLabel = (
  feature: { label?: string } | undefined,
  context: Record<string, unknown>
): string => String(jexlEvalString(feature?.label, context) ?? '')

/**
 * Feature header title: the feature's `title` only, evaluated when it is an expression.
 * There is deliberately NO fallback to the tab label or to the feature type: many features
 * should show no title in the header, and an empty result means exactly that.
 */
export const resolveFeatureTitle = (
  feature: Pick<FeatureTwin, 'title'> | undefined,
  context: Record<string, unknown>
): string => String(jexlEvalString(feature?.title, context) ?? '')

/** Header title runs for a feature, or `undefined` when the header should show no title. */
export const featureHeaderTitleRuns = (
  feature: Pick<FeatureTwin, 'title'> | undefined,
  context: Record<string, unknown>
): { text: string }[] | undefined => {
  const title = resolveFeatureTitle(feature, context)
  return title ? [{ text: title }] : undefined
}

/** Header title context and runs for the features that render through the shared FeatureHeader. */
export const createFeatureHeaderTitle = (
  feature: Pick<FeatureTwin, 'title'> | undefined,
  parameterValues: Record<string, unknown>
) => {
  const titleContext = createFeatureExpressionContext(parameterValues)
  return { titleContext, titleRuns: featureHeaderTitleRuns(feature, titleContext) }
}
